import React from 'react'
import {
    Box, Button, Typography, Grid, TextField, IconButton
} from '@material-ui/core'
import FormControl from '@material-ui/core/FormControl'
import FormGroup from '@material-ui/core/FormGroup'
import FormLabel from '@material-ui/core/FormLabel'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import ProviderAPI from 'config/api/ProviderAPI'
import { onError } from 'config/lib/errorLib'
import { productCategoryLabels, technologyCategoryFields, panelWarrantyOptions } from 'config/constants/Constants'
import { NotificationManager } from 'react-notifications'
import Loader from 'components/general/Loader'
import ReactDropzoneCropper from 'components/general/ReactDropzoneCropper'
import { Storage } from 'aws-amplify'
import config from 'config/api/Config'

// Normalise a category's stored value into an array of entries. Supports the
// legacy single-object shape ({ brand, ... }) as well as the new array shape.
export function normalizeEntries(value) {
    if (Array.isArray(value)) return value
    if (value && typeof value === 'object' && Object.keys(value).length > 0) return [value]
    return []
}

// True if an entry has any meaningful (non-empty) value.
function entryHasData(entry) {
    return entry && Object.values(entry).some(v => v !== undefined && v !== null && String(v).trim() !== '')
}

// Renders a single technology category that supports multiple entries. Each
// entry has a Brand selector, the category-specific fields and a product image,
// plus add/remove controls. Updates the whole category array via updateField.
function TechnologyCategorySection(props) {
    const { cat, companyId, brands = [], technology = {}, updateField, warrantyDefault = '' } = props
    const api = new ProviderAPI()
    const label = productCategoryLabels[cat] || cat
    const fields = technologyCategoryFields[cat] || [{ key: 'description', label: 'Description', type: 'text', placeholder: '' }]

    const [uploading, setUploading] = React.useState({})

    const stored = normalizeEntries(technology[cat])
    // Always show at least one entry to edit.
    const entries = stored.length > 0 ? stored : [{}]

    function setEntries(nextEntries) {
        updateField(`technology.${cat}`, nextEntries)
    }

    function updateEntry(index, key, value) {
        const next = entries.map((e, i) => (i === index ? { ...e, [key]: value } : e))
        setEntries(next)
    }

    function addEntry() {
        setEntries([...entries, {}])
    }

    function removeEntry(index) {
        const next = entries.filter((_, i) => i !== index)
        setEntries(next.length > 0 ? next : [])
    }

    function getBrandLogo(brandName) {
        const brandObj = brands.find(b => b.brandName === brandName && b.categories.includes(cat))
        return brandObj?.logo || null
    }

    async function uploadImage(index, image) {
        const uploadKey = `${cat}-${index}`
        setUploading(prev => ({ ...prev, [uploadKey]: true }))
        try {
            const imagePath = `providers/${companyId}/technology/${cat}/${index}`
            await Storage.put(imagePath, image, {
                contentType: image.type,
                level: 'public'
            })
            const imageUrl = `https://${config.s3.BUCKET}.s3.${config.s3.REGION}.amazonaws.com/public/${imagePath}`
            const next = entries.map((e, i) => (i === index ? { ...e, image: imageUrl } : e))
            updateField(`technology.${cat}`, next)
            // Persist immediately so the uploaded image is not lost.
            await api.updateTechnology(companyId, { ...technology, [cat]: next })
            NotificationManager.success(`${label} image uploaded`)
        } catch (e) {
            onError(e)
            NotificationManager.error('Failed to upload image')
        }
        setUploading(prev => ({ ...prev, [uploadKey]: false }))
    }

    function renderBrandSelect(index, entry) {
        const selectedBrand = entry.brand || ''
        return (
            <Select
                value={selectedBrand}
                onChange={(e) => updateEntry(index, 'brand', e.target.value)}
                input={<OutlinedInput />}
                displayEmpty
                renderValue={(value) => {
                    if (!value) return <span style={{ color: '#aaa' }}>Select a brand</span>
                    const brandObj = brands.find(b => b.brandName === value && b.categories.includes(cat))
                    return (
                        <Box display="flex" alignItems="center">
                            {brandObj?.logo && (
                                <img
                                    src={brandObj.logo}
                                    alt={brandObj.brandName}
                                    style={{ width: 24, height: 24, marginRight: 8, borderRadius: 4, objectFit: 'cover', border: '1px solid #eee', background: '#fff' }}
                                />
                            )}
                            {brandObj?.brandName || value}
                        </Box>
                    )
                }}
            >
                <MenuItem value="">
                    <em>None (clear selection)</em>
                </MenuItem>
                {brands.filter(b => b.categories.includes(cat)).map((brand) => (
                    <MenuItem key={brand.brandName} value={brand.brandName}>
                        <Box display="flex" alignItems="center">
                            {brand.logo && (
                                <img
                                    src={brand.logo}
                                    alt={brand.brandName}
                                    style={{ width: 24, height: 24, marginRight: 8, borderRadius: 4, objectFit: 'cover', border: '1px solid #eee', background: '#fff' }}
                                />
                            )}
                            {brand.brandName}
                        </Box>
                    </MenuItem>
                ))}
            </Select>
        )
    }

    function renderField(index, entry, field) {
        if (field.type === 'warranty') {
            const warrantyOptions = field.options || panelWarrantyOptions
            return (
                <Select
                    value={entry.warranty || warrantyDefault}
                    onChange={(e) => updateEntry(index, 'warranty', e.target.value)}
                    input={<OutlinedInput />}
                    style={{ marginBottom: '0.5rem' }}
                >
                    {warrantyOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                </Select>
            )
        }
        if (field.type === 'select') {
            return (
                <Select
                    value={entry[field.key] || ''}
                    onChange={(e) => updateEntry(index, field.key, e.target.value)}
                    input={<OutlinedInput />}
                    displayEmpty
                    style={{ marginBottom: '0.5rem' }}
                >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {(field.options || []).map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                </Select>
            )
        }
        return (
            <TextField
                type={field.type === 'number' ? 'number' : 'text'}
                variant='outlined'
                fullWidth
                placeholder={field.placeholder || ''}
                value={entry[field.key] || ''}
                onChange={(e) => updateEntry(index, field.key, e.target.value)}
            />
        )
    }

    function renderImageUpload(index, entry) {
        const imageUrl = entry.image || ''
        const isUploading = uploading[`${cat}-${index}`]
        return (
            <FormGroup>
                <FormLabel>Product Image</FormLabel>
                <Box mt={1} mb={1}>
                    <Typography variant="body2" style={{ color: '#8898aa', fontSize: '0.8rem' }}>
                        Upload a custom image for your {label.toLowerCase()} to display on your public profile
                    </Typography>
                </Box>
                <FormControl variant="filled" component={Box} width="100%">
                    {imageUrl && (
                        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" width="100%" mb={2}>
                            <img
                                src={imageUrl + '?v=' + Date.now()}
                                alt={`${label} Preview`}
                                style={{ maxHeight: '120px', maxWidth: '100%', borderRadius: '4px', marginBottom: '0.5rem' }}
                            />
                        </Box>
                    )}
                    {isUploading ? (
                        <Box display="flex" justifyContent="center" py={2}>
                            <Loader height={20} />
                        </Box>
                    ) : (
                        <ReactDropzoneCropper
                            updateImage={(file) => uploadImage(index, file)}
                            noPreview
                            button
                            uploadText={imageUrl ? 'Change Image' : 'Upload Image'}
                        />
                    )}
                </FormControl>
            </FormGroup>
        )
    }

    return (
        <>
            <Grid item xs={12}>
                <Box display='flex' alignItems='center' justifyContent='space-between'>
                    <h3 style={{ margin: 0 }}>{label}</h3>
                    <Button size='small' startIcon={<AddIcon />} onClick={addEntry}>
                        Add {label}
                    </Button>
                </Box>
            </Grid>
            {entries.map((entry, index) => (
                <React.Fragment key={index}>
                    {entries.length > 1 && (
                        <Grid item xs={12}>
                            <Box display='flex' alignItems='center' justifyContent='space-between'>
                                <Typography variant='subtitle2' style={{ color: '#8898aa' }}>
                                    {label} #{index + 1}
                                </Typography>
                                <IconButton size='small' onClick={() => removeEntry(index)} aria-label={`Remove ${label} ${index + 1}`}>
                                    <DeleteIcon fontSize='small' />
                                </IconButton>
                            </Box>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={4}>
                        <FormGroup>
                            <FormLabel>Brand</FormLabel>
                            <FormControl fullWidth>
                                {renderBrandSelect(index, entry)}
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    {fields.map(field => (
                        <Grid item xs={12} sm={4} key={field.key}>
                            <FormGroup>
                                <FormLabel>{field.label}</FormLabel>
                                {renderField(index, entry, field)}
                            </FormGroup>
                        </Grid>
                    ))}
                    <Grid item xs={12} sm={4}>
                        {renderImageUpload(index, entry)}
                    </Grid>
                </React.Fragment>
            ))}
        </>
    )
}

// Remove empty entries from a technology object before saving.
export function cleanTechnology(technology = {}) {
    const cleaned = {}
    Object.keys(technology).forEach(cat => {
        const arr = normalizeEntries(technology[cat]).filter(entryHasData)
        if (arr.length > 0) cleaned[cat] = arr
    })
    return cleaned
}

export default TechnologyCategorySection
